import { getToken } from '@nielsen-media/ui-toolkit-vue-api';

const MAV_BASE_URL = process.env.VUE_APP_MAV_API_URL;
const MAV_API_KEY = process.env.VUE_APP_MAV_API_KEY;

/**
 * Helper to build authenticated headers for MAV API calls.
 */
async function getHeaders() {
  const token = await getToken();
  if (!token) throw new Error('Unable to retrieve auth token');
  if (!MAV_BASE_URL) throw new Error('VUE_APP_MAV_API_URL is not configured');
  if (!MAV_API_KEY) throw new Error('VUE_APP_MAV_API_KEY is not configured');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-API-Key': MAV_API_KEY,
  };
}

/**
 * Submit a synchronous batch prediction.
 * POST /api/v1/predict/domain/batch
 *
 * @param {Array<{domain: string, countries?: string}>} domains
 * @returns {Promise<{success: boolean, total: number, succeeded: number, failed: number, results: Array}>}
 */
export async function submitSyncBatch(domains) {
  const headers = await getHeaders();
  const response = await fetch(`${MAV_BASE_URL}/predict/domain/batch`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ domains }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Batch request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Submit an asynchronous batch prediction.
 * POST /api/v1/predict/domain/batch/async
 *
 * @param {Array<{domain: string, countries?: string}>} domains
 * @param {string} [idempotencyKey] - Optional idempotency key for safe retries
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.autoCreate] - When true, domains that can't be matched to existing
 *   hierarchy will be retried with automatic Parent → Brand → Channel creation.
 * @returns {Promise<{batch_id: string, status: string, submitted_at: string, total: number}>}
 */
export async function submitAsyncBatch(domains, idempotencyKey, options = {}) {
  const headers = await getHeaders();
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  const body = { domains };
  if (options.autoCreate) {
    body.auto_create = true;
  }

  const response = await fetch(`${MAV_BASE_URL}/predict/domain/batch/async`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || 'Too many concurrent batches'), {
      retryAfter: body.retry_after_seconds || 60,
      status: 429,
    });
  }

  if (response.status === 409) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || 'Idempotency key conflict'), { status: 409 });
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Async batch request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Get the status of an async batch.
 * GET /api/v1/predict/domain/batch/async/{batch_id}
 *
 * @param {string} batchId
 * @returns {Promise<{batch_id: string, status: string, total: number, succeeded: number, failed: number, results: Array}>}
 */
export async function getBatchStatus(batchId) {
  const headers = await getHeaders();
  const response = await fetch(`${MAV_BASE_URL}/predict/domain/batch/async/${batchId}`, {
    method: 'GET',
    headers,
  });

  if (response.status === 404) {
    throw Object.assign(new Error('Batch not found'), { status: 404 });
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Status request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Connect to the SSE events stream for an async batch.
 * GET /api/v1/predict/domain/batch/async/{batch_id}/events
 *
 * Returns an object with:
 *  - onStatus(callback): register for status events
 *  - onItem(callback): register for per-item completion events
 *  - onError(callback): register for connection errors
 *  - close(): disconnect the stream
 *
 * @param {string} batchId
 * @returns {Promise<{onStatus: Function, onItem: Function, onError: Function, close: Function}>}
 */
export async function connectBatchEvents(batchId) {
  const token = await getToken();
  const url = new URL(`${MAV_BASE_URL}/predict/domain/batch/async/${batchId}/events`);
  url.searchParams.set('token', token);
  url.searchParams.set('apikey', MAV_API_KEY);

  let active = true;
  let eventSource = null;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  const MAX_RECONNECT_ATTEMPTS = 10;
  const BASE_RECONNECT_DELAY_MS = 1000;
  const MAX_RECONNECT_DELAY_MS = 30000;

  const callbacks = { status: [], item: [], error: [] };
  // Buffer events that arrive before callbacks are registered
  const buffer = { status: [], item: [] };
  let buffering = true;

  function emit(type, data) {
    if (callbacks[type].length > 0) {
      callbacks[type].forEach(cb => cb(data));
    } else if (buffering) {
      buffer[type].push(data);
    }
  }

  function flushBuffer(type) {
    buffer[type].forEach(data => callbacks[type].forEach(cb => cb(data)));
    buffer[type] = [];
  }

  function connect() {
    // Close any lingering previous connection before opening a new one
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }

    eventSource = new EventSource(url.toString());

    eventSource.addEventListener('status', event => {
      reconnectAttempts = 0;
      if (event.data) {
        try {
          const parsed = JSON.parse(event.data);
          emit('status', parsed);
        } catch (_) {
          // skip malformed data
        }
      }
    });

    eventSource.addEventListener('item', event => {
      reconnectAttempts = 0;
      if (event.data) {
        try {
          const parsed = JSON.parse(event.data);
          emit('item', parsed);
        } catch (_) {
          // skip malformed data
        }
      }
    });

    eventSource.addEventListener('heartbeat', () => {
      reconnectAttempts = 0;
    });

    eventSource.onopen = () => {
      reconnectAttempts = 0;
    };

    eventSource.onerror = () => {
      if (!active) return;

      // EventSource auto-reconnects for transient errors (readyState = CONNECTING).
      // Only intervene when the browser has given up (readyState = CLOSED).
      if (eventSource.readyState === EventSource.CLOSED) {
        eventSource.close();
        eventSource = null;
        reconnectAttempts += 1;

        if (reconnectAttempts <= MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts - 1),
            MAX_RECONNECT_DELAY_MS,
          );
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            if (active) {
              connect();
            }
          }, delay);
        } else {
          const err = new Error(
            `SSE connection lost after ${MAX_RECONNECT_ATTEMPTS} reconnection attempts`,
          );
          callbacks.error.forEach(cb => cb(err));
        }
      }
    };
  }

  // Verify the endpoint is reachable before opening the SSE stream.
  // Let network errors propagate so callers get a clear failure upfront.
  const headers = await getHeaders();
  const probeResponse = await fetch(
    `${MAV_BASE_URL}/predict/domain/batch/async/${batchId}/events`,
    {
      method: 'HEAD',
      headers: {
        ...headers,
        Accept: 'text/event-stream',
      },
    },
  );

  if (!probeResponse.ok) {
    let errorMessage = `Events stream failed: ${probeResponse.status}`;
    try {
      const contentType = probeResponse.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const body = await probeResponse.json();
        errorMessage = body.error || errorMessage;
      } else {
        const text = await probeResponse.text();
        if (text) errorMessage = text;
      }
    } catch (_) {
      // use the default error message
    }
    throw new Error(errorMessage);
  }

  connect();

  return {
    onStatus(cb) {
      callbacks.status.push(cb);
      flushBuffer('status');
    },
    onItem(cb) {
      callbacks.item.push(cb);
      flushBuffer('item');
    },
    onError(cb) {
      callbacks.error.push(cb);
    },
    close() {
      active = false;
      buffering = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
      callbacks.status = [];
      callbacks.item = [];
      callbacks.error = [];
      buffer.status = [];
      buffer.item = [];
    },
  };
}
/**
 * Fetch session history for the agentic prediction flow.
 * GET /api/v1/sessions/history
 *
 * @returns {Promise<{sessions: Array}>}
 */
export async function getSessionHistory() {
  const headers = await getHeaders();
  const response = await fetch(`${MAV_BASE_URL}/sessions`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Session history request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch a single session's details.
 * GET /api/v1/sessions/history/:session_id
 *
 * @param {string} sessionId
 * @returns {Promise<Object>} - Full session object with prediction and proposals
 */
export async function getSessionDetail(sessionId) {
  const headers = await getHeaders();
  const response = await fetch(`${MAV_BASE_URL}/sessions/${sessionId}`, {
    method: 'GET',
    headers,
  });

  if (response.status === 404) {
    throw Object.assign(new Error('Session not found'), { status: 404 });
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Session detail request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Cancel an in-flight async batch.
 * POST /api/v1/predict/domain/batch/async/{batch_id}/cancel
 *
 * @param {string} batchId
 * @returns {Promise<void>}
 */
export async function cancelBatch(batchId) {
  const headers = await getHeaders();
  const response = await fetch(`${MAV_BASE_URL}/predict/domain/batch/async/${batchId}/cancel`, {
    method: 'POST',
    headers,
  });

  if (response.status === 409) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || 'Batch already in terminal state'), {
      status: 409,
    });
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Cancel request failed: ${response.status}`);
  }
}

/**
 * Record an edit (user action) on a batch item.
 * POST /api/v1/predict/domain/batch/async/{batch_id}/items/{item_index}/edits
 *
 * Sends the action_type format: { action_type, ...fields }
 * The backend maps action_type to the internal edit_type automatically.
 *
 * @param {string} batchId - The batch UUID
 * @param {number} itemIndex - Zero-based index of the item within the batch
 * @param {string} actionType - One of: parent_created, brand_created, channel_created, hierarchy_created, prediction_accepted, prediction_override, rule_created
 * @param {Object} data - Action-specific fields (flat, merged into request body)
 * @returns {Promise<Object>}
 */
export async function recordBatchItemEdit(batchId, itemIndex, actionType, data = {}) {
  const headers = await getHeaders();
  const body = { action_type: actionType, ...data };

  const response = await fetch(
    `${MAV_BASE_URL}/predict/domain/batch/async/${batchId}/items/${itemIndex}/edits`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const respBody = await response.json().catch(() => ({}));
    throw Object.assign(new Error(respBody.error || `Record edit failed: ${response.status}`), {
      status: response.status,
    });
  }

  return response.json();
}

/**
 * Get all edits recorded for a batch item.
 * GET /api/v1/predict/domain/batch/async/{batch_id}/items/{item_index}/edits
 *
 * @param {string} batchId - The batch UUID
 * @param {number} itemIndex - Zero-based index of the item within the batch
 * @returns {Promise<{edits: Array<{edit_type: string, metadata: Object, operator_user_id: string, created_at: string}>}>}
 */
export async function getBatchItemEdits(batchId, itemIndex) {
  const headers = await getHeaders();
  const response = await fetch(
    `${MAV_BASE_URL}/predict/domain/batch/async/${batchId}/items/${itemIndex}/edits`,
    {
      method: 'GET',
      headers,
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || `Get edits failed: ${response.status}`), {
      status: response.status,
    });
  }

  return response.json();
}

/**
 * List unified predictions (paginated, filterable).
 * GET /api/v1/predictions/unified
 *
 * @param {Object} [params] - Query parameters
 * @param {number} [params.page=1] - Page number (1-indexed)
 * @param {number} [params.page_size=50] - Results per page (max 200)
 * @param {string} [params.domain] - Partial domain match (case-insensitive)
 * @param {number} [params.confidence_min] - Min overall confidence (0.0–1.0)
 * @param {number} [params.confidence_max] - Max overall confidence (0.0–1.0)
 * @param {string} [params.outcome] - Exact match on validation_type (e.g. "valid", "adult", "parked")
 * @returns {Promise<{results: Array, total_count: number, page: number, page_size: number, total_pages: number}>}
 */
export async function getUnifiedPredictions(params = {}) {
  const headers = await getHeaders();
  const url = new URL(`${MAV_BASE_URL}/predictions/unified`);

  const ALLOWED_PARAMS = new Set([
    'page',
    'page_size',
    'domain',
    'confidence_min',
    'confidence_max',
    'outcome',
  ]);
  Object.entries(params).forEach(([key, value]) => {
    if (!ALLOWED_PARAMS.has(key)) return;
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (typeof value === 'number' && isNaN(value))
    )
      return;
    url.searchParams.set(key, value);
  });

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(
      new Error(body.error || `Unified predictions request failed: ${response.status}`),
      {
        status: response.status,
      },
    );
  }

  return response.json();
}

/**
 * Get prediction for a specific domain.
 * GET /api/v1/predictions/unified/:domain
 *
 * @param {string} domain - The domain name (e.g. example.com)
 * @returns {Promise<Object>} - Single prediction object
 */
export async function getUnifiedPrediction(domain) {
  if (!domain || typeof domain !== 'string' || !domain.trim()) {
    throw new Error('getUnifiedPrediction: a non-empty domain string is required');
  }
  const headers = await getHeaders();
  const response = await fetch(
    `${MAV_BASE_URL}/predictions/unified/${encodeURIComponent(domain.trim())}`,
    {
      method: 'GET',
      headers,
    },
  );

  if (response.status === 404) {
    throw Object.assign(new Error('No prediction found for this domain'), { status: 404 });
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(
      new Error(body.error || `Unified prediction request failed: ${response.status}`),
      {
        status: response.status,
      },
    );
  }

  return response.json();
}

/**
 * List all async batches owned by the current caller.
 * GET /api/v1/predict/domain/batch/async
 *
 * Returns an array of batch summary objects. The server scopes results
 * to the authenticated user (owner). Used on page reload to discover
 * in-flight or recently completed batches.
 *
 * @returns {Promise<Array<{batch_id: string, status: string, total: number, succeeded: number, failed: number, submitted_at: string}>>}
 */
export async function listBatches() {
  const headers = await getHeaders();
  const response = await fetch(`${MAV_BASE_URL}/predict/domain/batch/async`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `List batches failed: ${response.status}`);
  }

  const data = await response.json();
  // Server wraps the list in {"batches": [...]}
  const list = data.batches ?? data;
  if (!Array.isArray(list)) {
    throw new Error('Unexpected response shape from listBatches: expected an array');
  }
  return list;
}

/**
 * Resume a paused session by session ID.
 * POST /api/v2/predict/domain/resume
 *
 * Used to reconnect to sessions awaiting OWL approval or user choice.
 * Backend checks OWL status and continues if approved.
 *
 * @param {string} sessionId - The session ID to resume
 * @returns {Promise<Response>} - EventSource-compatible response for SSE streaming
 */
export async function resumeSession(sessionId) {
  const token = await getToken();
  if (!token) throw new Error('Unable to retrieve auth token');
  if (!MAV_BASE_URL) throw new Error('VUE_APP_MAV_API_URL is not configured');
  if (!MAV_API_KEY) throw new Error('VUE_APP_MAV_API_KEY is not configured');

  const MAV_V2_URL = MAV_BASE_URL.replace(/\/v1\/?$/, '/v2');

  const response = await fetch(`${MAV_V2_URL}/predict/domain/resume`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-API-Key': MAV_API_KEY,
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ session_id: sessionId }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Resume session failed: ${response.status}`);
  }

  return response;
}
